import { cn } from '@/helpers/classnames';
import Input from '../core/Input';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReactNode, useCallback } from 'react';

interface InputPhoneFieldProps<
  T extends FieldValues,
> extends UseControllerProps<T> {
  label?: string;
  editMode?: boolean;
  placeholder?: string;
  noValueDescription?: ReactNode;
  error?: FieldError;
  helperText?: string;
  helperTextReserveSpace?: boolean;
  onBlur?: () => void;
  className?: string;
  required?: boolean;
}

export const InputPhone = <T extends FieldValues>({
  label,
  editMode,
  placeholder,
  noValueDescription,
  error,
  helperText,
  helperTextReserveSpace = false,
  onBlur,
  className,
  required,
  ...props
}: InputPhoneFieldProps<T>) => {
  const { field } = useController(props);
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (value: string) => {
      let cleaned = value.replace(/[\s-]/g, '').replace(/[^\d+]/g, '');

      if (cleaned.includes('+')) {
        const parts = cleaned.split('+');
        cleaned = '+' + parts.filter((p) => p).join('');
      }

      field.onChange(cleaned);
    },
    [field],
  );

  const handleBlur = () => {
    field.onBlur();
    onBlur?.();
  };

  return editMode === true ? (
    <Input
      id={field.name}
      type="text"
      value={field.value || ''}
      placeholder={placeholder || '+46701234567'}
      className={className}
      label={label}
      onChange={(e) => handleChange(e.target.value)}
      error={error?.message}
      onBlur={handleBlur}
      helperText={helperText}
      helperTextReserveSpace={helperTextReserveSpace}
      required={required}
    />
  ) : (
    <span className={cn(!field.value && 'italic text-text-700', className)}>
      {field.value ||
        noValueDescription ||
        formatMessage({ defaultMessage: 'Inget värde' })}
    </span>
  );
};
